import { type ToolCallMessagePartComponent, useAui } from '@assistant-ui/react';
import { useEffect } from 'react';
import type z from 'zod';

import { DefaultToolDisplayRender } from '#plugin-chat/components/tool-display/DefaultToolDisplayRender.tsx';
import { renderInstructions } from '#plugin-chat/lib/instructions/reconciler.ts';
import { commitPendingBinding, stripPendingBinding } from '#plugin-chat/lib/tool/datasetBinding.ts';
import { toToolErrorResponse } from '#plugin-chat/lib/tool/errors/index.ts';
import type { ToolDisplaySpec, ToolMessageFormatters } from '#plugin-chat/lib/tool/message-formatters.ts';
import { parseResultEnvelope } from '#plugin-chat/lib/tool/result-envelope.ts';

export function Tool<ArgsType extends Record<string, unknown>, ResultType>({
  name,
  display,
  render,
  description,
  parameters,
  execute,
  message,
  children,
}: React.PropsWithChildren<{
  name: string;
  display?: ToolDisplaySpec<ArgsType, ResultType>;
  // Chrome-supplied props (currently `{ args }`); tool authors pick
  // out the fields they need. Loose typing keeps existing call sites
  // happy until the framework's render-prop shape stabilises.
  render?: ToolCallMessagePartComponent<ArgsType, ResultType>;
  message?: ToolMessageFormatters<ArgsType, ResultType>;
  description?: React.ReactNode;
  parameters: z.ZodType;
  disabled?: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: ToolExecuteFunction not yet typed
  execute: any;
}>) {
  const aui = useAui();

  useEffect(() => {
    console.debug(`[Tool] Registering tool: ${name}`, {
      name,
      display,
      render,
      description,
      parameters,
      execute,
      message,
    });
    const unsetToolUi = aui.tools().setToolUI(name, render ?? (display ? DefaultToolDisplayRender : () => null));

    // Execute boundary: a thrown `execute` must reach the transcript as
    // the structured `{ success: false, … }` envelope, otherwise the raw
    // error lands on the tool part with no failure marker, is
    // misclassified as a success by `parseResultEnvelope`, and is
    // silently dropped — the LLM never learns the tool failed and blindly
    // re-issues the same call. This is the single choke point every
    // `<Tool>` flows through and it is mandatory: no tool may bypass it.
    const safeExecute = async (...execArgs: unknown[]) => {
      let result: unknown;
      try {
        result = await execute(...execArgs);
      } catch (err) {
        console.error(`[Tool] execute threw for "${name}" — normalising to error envelope`, err);
        return toToolErrorResponse(err);
      }
      // Producers stage their dataset binding onto the success envelope
      // rather than mutating the session inside `execute`. Commit it
      // here — the single choke point — ONLY when the result is a
      // success envelope, as the final step before returning. A call the
      // LLM is told failed therefore can never leave or persist a
      // binding, by construction. A rebind clash throws from the binding
      // store *before* it mutates, so nothing is committed; route it to
      // the structured error envelope like any other execute failure.
      if (parseResultEnvelope(result).kind === 'success') {
        try {
          return commitPendingBinding(result);
        } catch (err) {
          console.error(`[Tool] binding commit failed for "${name}" — normalising to error envelope`, err);
          return toToolErrorResponse(err);
        }
      }
      return stripPendingBinding(result);
    };

    const unregisterToolEvent = aui.modelContext().register({
      getModelContext: () => ({
        tools: {
          [name]: {
            description: renderInstructions(description),
            parameters: parameters.toJSONSchema({ unrepresentable: 'any' }),
            execute: safeExecute,
            // Additional framework utils for chrome rendering — `message`
            // is a `{ result?: (envelope) => ReactNode }` formatter the
            // chrome uses to build the LLM-visible result message; we
            // pass it through unmodified for the chrome to consume via
            // its own `useToolMessage`.
            message,
            display,
          },
        },
      }),
    });

    return () => {
      console.debug(`[Tool] Unregistering tool: ${name}`);
      unregisterToolEvent();
      unsetToolUi();
    };
  }, [aui, name, display, render, description, parameters, execute, message]);

  return children;
}
